import {
  CardContentDto,
  CriteriaCardDto,
  DescriptionCardDto,
  EmployeeObservationsDto,
  ManageVacationsCriteriaDto,
  PagesCardTabDto,
  PeopleCardDto,
  SeasonCalendarPeriodDto,
  SeasonCalendarDayTypeDto,
  SeasonCalendarDayDto,
  SeasonCalendarDto,
  SeasonCalendarMonthDto,
  SeasonCalendarWeekDto,
  SeasonDto,
  ShapeCardDto,
  TabCardDto,
  VacationsSeasonDto,
  SeasonCalendarCaptionsDto,
  SeasonCalendarSeasonColourDto,
  SeasonCalendarEntitlementDetailDto,
  SeasonCalendarEventDto
} from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import { ManageVacationsUserType, SEASONS } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import {
  CardContent,
  CriteriaCard,
  DescriptionCard,
  ManageVacationsCriteria,
  PagesCardTab,
  PeopleCard,
  ShapeCard,
  TabCard
} from '@app/entities/models/manage-vacations/manage-vacations-criteria.model';
import {
  EmployeeObservations,
  Proposal,
  Season,
  SeasonCalendar,
  SeasonCalendarCaptions,
  SeasonCalendarDayType,
  SeasonCalendarDay,
  SeasonCalendarMonth,
  SeasonCalendarWeek,
  VacationsSeason,
  CalendarEntitlementDetail,
  SeasonCalendarEventShape
} from '@app/entities/models/manage-vacations/manage-vacations.model';
import { TUserTypeCalendarProposal } from '@app/entities/types/manage-vacations.type';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

import { mapLogisticPeriods } from './logistics/logistics-manage-vacations.mapper';
import { mapMomoPeriods } from './momo/momo-manage-vacations.mapper';
import { mapMotFactoryPeriods } from './mot/mot-manage-vacations.mapper';

const mapShapeCard: MapFromFn<ShapeCardDto, ShapeCard> = (value) =>
  Builder.forModel(ShapeCard).type(value.type).color(value.color).build();

const mapDescriptionCard: MapFromFn<DescriptionCardDto, DescriptionCard> = (value) =>
  Builder.forModel(DescriptionCard).subtitle(value.subtitle).value(value.value).build();

const mapCardContent: MapFromFn<CardContentDto, CardContent> = (value) =>
  Builder.forModel(CardContent).contentImg(value.content_img).text(value.text).build();

const mapPeopleCard: MapFromFn<PeopleCardDto, PeopleCard> = (value) =>
  Builder.forModel(PeopleCard).subtitle(value.subtitle).person(value.person.map(mapCardContent)).build();

const mapPagesCardTab: MapFromFn<PagesCardTabDto, PagesCardTab> = (value) =>
  Builder.forModel(PagesCardTab).key(value.key).content(value.content.map(mapCardContent)).build();

const mapTabCard: MapFromFn<TabCardDto, TabCard> = (value) =>
  Builder.forModel(TabCard).subtitle(value.subtitle).pages(value.pages.map(mapPagesCardTab)).build();

const mapManageVacationsCriteriaCard: MapFromFn<CriteriaCardDto, CriteriaCard> = (value) =>
  Builder.forModel(CriteriaCard)
    .shape(value.shape ? mapShapeCard(value.shape) : undefined)
    .title(value.title)
    .desc(value.desc ? value.desc.map(mapDescriptionCard) : [])
    .people(value.people ? mapPeopleCard(value.people) : undefined)
    .tabs(value.tabs ? mapTabCard(value.tabs) : undefined)
    .order(value.order)
    .build();

export const mapManageVacationsCriteria: MapFromFn<ManageVacationsCriteriaDto, ManageVacationsCriteria> = (value) =>
  value.criteria.map(mapManageVacationsCriteriaCard);

const mapSeason: MapFromFn<SeasonDto, Season> = (value) =>
  Builder.forModel(Season)
    .id(value.id as SEASONS)
    .title(value.title)
    .description(value.description)
    .startDate(value.start_date ?? '')
    .endDate(value.end_date ?? '')
    .build();

export const mapVacationsSeason: MapFromFn<VacationsSeasonDto, VacationsSeason> = (value) => value.map(mapSeason);

export const mapObservations: MapFromFn<EmployeeObservationsDto, EmployeeObservations> = (value) =>
  Builder.forModel(EmployeeObservations)
    .id(value.id ?? 0)
    .content(value.content)
    .build();

export const mapPeriods =
  (userType: ManageVacationsUserType): MapFromFn<SeasonCalendarPeriodDto, Proposal> =>
  (value) => {
    switch (userType) {
      case ManageVacationsUserType.MOT:
      case ManageVacationsUserType.BREAD_FACTORY:
      case ManageVacationsUserType.PALLET_FACTORY:
        return mapMotFactoryPeriods(value);
      case ManageVacationsUserType.MOMO:
        return mapMomoPeriods(value);
      case ManageVacationsUserType.LOGISTICS:
        return mapLogisticPeriods(value);
    }
  };

export const mapSeasonCalendarMonth =
  (
    userType: ManageVacationsUserType
  ): MapFromFn<SeasonCalendarMonthDto, SeasonCalendarMonth<TUserTypeCalendarProposal>> =>
  (value) =>
    Builder.forModel(SeasonCalendarMonth<TUserTypeCalendarProposal>)
      .month(value.month)
      .slots(value.periods.map(mapPeriods(userType)))
      .weeks(value.weeks.map(mapSeasonCalendarWeek))
      .year(value.year)
      .build();

export const mapSeasonCalendar =
  (userType: ManageVacationsUserType): MapFromFn<SeasonCalendarDto, SeasonCalendar<TUserTypeCalendarProposal>> =>
  (value) =>
    Builder.forModel(SeasonCalendar<TUserTypeCalendarProposal>)
      .calendar(value.calendar.map(mapSeasonCalendarMonth(userType)))
      .colours(value.season_colours ? mapSeasonCalendarColour(value.season_colours) : undefined)
      .captions(value.captions.map(mapSeasonCalendarCaption))
      .maxHolidays(value.entitlement?.max)
      .minHolidays(value.entitlement?.min)
      .numPeriods(value.entitlement?.num_periods)
      .detail(value.entitlement?.detail ? mapCalendarEntitlementDetail(value.entitlement.detail) : null)
      .build();

export const mapSeasonCalendarCaption: MapFromFn<SeasonCalendarCaptionsDto, SeasonCalendarCaptions> = (value) =>
  Builder.forModel(SeasonCalendarCaptions)
    .id(value.id)
    .name(value.name)
    .colour(value.colour)
    .onlyBorder(value.only_border)
    .shape(value.shape)
    .build();

export const mapSeasonCalendarColour: MapFromFn<SeasonCalendarSeasonColourDto, SeasonCalendarDayType> = (value) =>
  Builder.forModel(SeasonCalendarDayType)
    .primaryColour(value.primary_colour)
    .secondaryColour(value.secondary_colour)
    .primaryShape(value.primary_shape)
    .secondaryShape(value.secondary_shape)
    .build();

export const mapSeasonCalendarDay: MapFromFn<SeasonCalendarDayDto, SeasonCalendarDay> = (value) =>
  Builder.forModel(SeasonCalendarDay)
    .day(value.day)
    .date(value.date)
    .dayType(value.day_type ? mapSeasonCalendarDayType(value.day_type) : null)
    .dayEvents(value.events ? value.events.map(mapSeasonCalendarDayEvent) : null)
    .build();

export const mapSeasonCalendarDayType: MapFromFn<SeasonCalendarDayTypeDto, SeasonCalendarDayType> = (value) =>
  Builder.forModel(SeasonCalendarDayType)
    .ids(value.ids)
    .name(value.name)
    .primaryColour(value.primary_colour)
    .secondaryColour(value.secondary_colour)
    .primaryShape(value.primary_shape)
    .secondaryShape(value.secondary_shape)
    .isWorkingDay(value.is_working_day)
    .isClosedHoliday(value.is_closed_holiday)
    .isCountable(value.is_countable)
    .build();

export const mapSeasonCalendarWeek: MapFromFn<SeasonCalendarWeekDto, SeasonCalendarWeek> = (value) =>
  Builder.forModel(SeasonCalendarWeek).days(value.days.map(mapSeasonCalendarDay)).build();

export const mapSeasonCalendarDayEvent: MapFromFn<SeasonCalendarEventDto, SeasonCalendarEventShape> = (value) =>
  Builder.forModel(SeasonCalendarEventShape)
    .type(value.shape.type)
    .color(value.shape.color)
    .position(value.position)
    .build();

export const mapCalendarEntitlementDetail: MapFromFn<SeasonCalendarEntitlementDetailDto, CalendarEntitlementDetail> = (
  value
) =>
  Builder.forModel(CalendarEntitlementDetail)
    .saturday(value.saturday)
    .sunday(value.sunday)
    .workday(value.workday)
    .freeDueToPublicHoliday(value.free_due_to_public_holiday)
    .build();
